import React from 'react';
import * as Sentry from '@sentry/react';
import ServerErrorPage from '@/pages/ServerErrorPage';
import { ChunkReloadError } from '@/lib/lazyWithRetry';

interface ErrorBoundaryState {
  error: Error | null;
  errorId: string | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional custom fallback. If not provided, the ServerErrorPage is used. */
  fallback?: (state: ErrorBoundaryState, reset: () => void) => React.ReactNode;
}

/**
 * Top-level React error boundary.
 *
 * Catches render-time exceptions below it and shows a recovery UI instead of
 * letting React bubble them to the Suspense fallback — which the user reads as
 * "the page isn't loading". Also logs to console so Sentry/backend error
 * tracking can pick up stack traces.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, errorId: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      error,
      errorId: `err_${Date.now().toString(36)}`,
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // ChunkReloadError is intentional — the page is already reloading.
    // Skip Sentry capture and console noise for this expected case.
    if (error instanceof ChunkReloadError) return;

    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info.componentStack);
    // Direct Sentry capture. Previously we tried `window.Sentry?.
    // captureException` as a safety fallback, but the React SDK never
    // installs itself on window, so that path was always a no-op —
    // every render crash went uncaught in Issues. Using the imported
    // Sentry client directly guarantees capture. The React contexts
    // key is Sentry's recommended shape for component-stack metadata.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? '' } },
      tags: { source: 'ErrorBoundary' },
    });
  }

  reset = (): void => {
    this.setState({ error: null, errorId: null });
  };

  render(): React.ReactNode {
    if (this.state.error) {
      // ChunkReloadError means window.location.reload() has already been
      // called. Render nothing (null) while the browser navigates away so
      // the user sees a blank/loading screen rather than an error page.
      if (this.state.error instanceof ChunkReloadError) return null;

      if (this.props.fallback) return this.props.fallback(this.state, this.reset);
      // Shared ServerErrorPage (also reached via ErrorBoundary +
      // the 5xx-style surfaces in the rest of the app) — Sentry
      // capture still happens in componentDidCatch above, so the
      // visual swap here is purely presentation.
      return <ServerErrorPage />;
    }
    return this.props.children;
  }
}
